import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  TextInput,
  ActivityIndicator,
} from 'react-native';
import Svg, { Path, Circle, Rect } from 'react-native-svg';
import FontFamily from '../../../../../constants/fonts';
import { formatUserName } from '../../../../../utils/emailTouserName';
import { NewsIcon, PlusIcon, FavouriteStarIcon } from '../../StandupIcons';
import { NewsItem } from '../../../types';
import NewsCard from '../../../components/NewsCard';
import DotPagination from '../../../components/DotPagination';

const InfoIcon = () => (
  <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <Path d="M8 1.33334C4.32 1.33334 1.33334 4.32 1.33334 8C1.33334 11.68 4.32 14.6667 8 14.6667C11.68 14.6667 14.6667 11.68 14.6667 8C14.6667 4.32 11.68 1.33334 8 1.33334ZM8.66668 11.3333H7.33334V7.33334H8.66668V11.3333ZM8.66668 6H7.33334V4.66667H8.66668V6Z" fill="#C12C01" />
  </Svg>
);

interface NewsTabProps {
  newsTitle: string;
  newsDescription: string;
  standupNewsList: NewsItem[]; // News associated with this standup (for Selected News section)
  allNewsList: NewsItem[]; // All news (standup + global) for the feed section
  currentNewsPage: number;
  onNewsPageChange: (page: number) => void;
  onTitleChange: (text: string) => void;
  onDescriptionChange: (text: string) => void;
  onAddNews: () => void;
  onDeleteNews: (newsId: string) => void;
  deletingNewsId?: string | null;
  onToggleFavorite?: (newsId: number) => void;
  togglingNewsId?: number | null;
  onViewMore?: (newsId: string) => void;
  titleError?: boolean;
  descriptionError?: boolean;
  onTitleFocus?: () => void;
  onDescriptionFocus?: () => void;
  isAddingNews?: boolean;
}

export const NewsTab: React.FC<NewsTabProps> = ({
  newsTitle,
  newsDescription,
  standupNewsList,
  allNewsList,
  currentNewsPage,
  onNewsPageChange,
  onTitleChange,
  onDescriptionChange,
  onAddNews,
  onDeleteNews,
  deletingNewsId,
  onToggleFavorite,
  togglingNewsId,
  onViewMore,
  titleError = false,
  descriptionError = false,
  onTitleFocus,
  onDescriptionFocus,
  isAddingNews = false,
}) => {
  // Selected News: only show news items associated with this standup
  // (standupNewsList already contains standup-specific news and starred global news that got associated)
  const selectedNews = standupNewsList;

  // News feed: show all news (standup + global) so users can star global news
  const combinedNewsList = [...allNewsList];

  // Paginate selected news - 4 per page
  const newsPerPage = 4;
  const totalNewsPages = Math.ceil(selectedNews.length / newsPerPage);
  const paginatedNews = selectedNews.slice(
    currentNewsPage * newsPerPage,
    (currentNewsPage + 1) * newsPerPage
  );

  return (
    <View style={styles.newsContainer}>
      {/* News Display Section */}
      <View style={styles.newsDisplaySection}>
        <View style={styles.NewsdisplayHeader}>
                  <Text style={styles.NewsdisplayTitle}>Selected News</Text>
                </View>
        {selectedNews.length === 0 ? (
          <View style={styles.newsDisplayEmptyState}>
            <Text style={styles.newsDisplayEmptyText}>No news added</Text>
          </View>
        ) : (
          <>
            <View style={styles.newsCardsRow}>
              {paginatedNews.map((news) => {
                const newsIdNum = parseInt(news.id) || 0;
                const isTogglingThisNews = togglingNewsId === newsIdNum;
                return (
                  <NewsCard
                    key={news.id}
                    news_id={newsIdNum}
                    title={news.title}
                    description={news.description}
                    date={news.date}
                    is_favourite={news.is_favourite}
                    width="calc(25% - 9px)"
                    handleToggleFavorite={onToggleFavorite || (() => {})}
                    isLoading={isTogglingThisNews}
                    disabled={isTogglingThisNews}
                    is_truncated={news.is_truncated}
                    onViewMore={onViewMore ? () => onViewMore(news.id) : undefined}
                    datePosition="below-title"
                  />
                );
              })}
            </View>
            {totalNewsPages > 1 && (
              <View style={styles.newsPaginationContainer}>
                <DotPagination
                  totalDots={totalNewsPages}
                  activeDot={currentNewsPage}
                  onDotPress={onNewsPageChange}
                />
              </View>
            )}
          </>
        )}
      </View>

      {/* Existing News Form */}
      <View style={styles.newsFormContainer}>
        {/* Header */}
        <Text style={styles.newsHeader}>News</Text>

        {/* Main Content */}
        <View style={styles.newsContent}>
        {/* Left Side - Input Form */}
        <View style={styles.newsLeftCard}>
          <View style={styles.newsInputContainer}>
            <View>
              <TextInput
                style={[styles.newsTitleInput, titleError && styles.inputError]}
                placeholder="Title"
                placeholderTextColor="#B1B1B1"
                value={newsTitle}
                onChangeText={onTitleChange}
                onFocus={onTitleFocus}
                maxLength={50}
              />
              {titleError && (
                <View style={styles.errorMessageContainer}>
                  <InfoIcon />
                  <Text style={styles.errorMessageText}>This field is required to continue</Text>
                </View>
              )}
            </View>
            <TextInput
              style={[styles.newsDescriptionInput, descriptionError && styles.inputError]}
              placeholder={descriptionError ? "Please complete this field to continue" : "Description"}
              placeholderTextColor={descriptionError ? "#C12C01" : "#B1B1B1"}
              value={newsDescription}
              onChangeText={onDescriptionChange}
              onFocus={onDescriptionFocus}
              multiline
              textAlignVertical="top"
            />
            <TouchableOpacity
              style={[styles.addNewsButton, isAddingNews && styles.addNewsButtonDisabled]}
              onPress={onAddNews}
              activeOpacity={0.7}
              disabled={isAddingNews}
            >
              {isAddingNews ? (
                <ActivityIndicator size="small" color="#005CBC" />
              ) : (
                <PlusIcon />
              )}
              <Text style={styles.addNewsButtonText}>{isAddingNews ? 'Adding...' : 'Add to News'}</Text>
            </TouchableOpacity>
          </View>
        </View>

        {/* Right Side - News Feed */}
        <View style={styles.newsRightCard}>
          {combinedNewsList.length === 0 ? (
            // Empty State
            <View style={styles.newsEmptyState}>
              <View style={styles.newsEmptyCard}>
                <Svg width={400} height={200} viewBox="0 0 400 200" fill="none">
                  {/* Moon */}
                  <Circle cx="320" cy="50" r="25" fill="#E0E0E0" opacity="0.3" />
                  <Circle cx="315" cy="48" r="22" fill="#F5F5F5" />
                  
                  {/* Stars */}
                  <Circle cx="280" cy="40" r="2" fill="#E0E0E0" />
                  <Circle cx="350" cy="60" r="2" fill="#E0E0E0" />
                  <Circle cx="340" cy="30" r="1.5" fill="#E0E0E0" />
                  <Circle cx="365" cy="45" r="1.5" fill="#E0E0E0" />
                  
                  {/* Mountains - Background */}
                  <Path 
                    d="M0 140 L60 80 L90 110 L140 60 L190 100 L240 70 L290 110 L340 80 L400 120 L400 180 L0 180 Z" 
                    fill="#E8E8E8" 
                  />
                  
                  {/* Mountains - Foreground */}
                  <Path 
                    d="M0 160 L40 120 L80 145 L130 100 L180 130 L230 110 L280 140 L330 115 L380 145 L400 135 L400 180 L0 180 Z" 
                    fill="#D0D0D0" 
                  />
                  
                  {/* Trees */}
                  <Path d="M50 160 L55 145 L60 160 Z" fill="#C0C0C0" />
                  <Path d="M50 165 L55 150 L60 165 Z" fill="#C0C0C0" />
                  <Rect x="54" y="165" width="2" height="10" fill="#A0A0A0" />
                  
                  <Path d="M370 145 L375 130 L380 145 Z" fill="#C0C0C0" />
                  <Path d="M370 150 L375 135 L380 150 Z" fill="#C0C0C0" />
                  <Rect x="374" y="150" width="2" height="10" fill="#A0A0A0" />
                  
                  {/* Telescope */}
                  <Path 
                    d="M150 170 L155 160 L165 163 L160 173 Z" 
                    fill="#B0B0B0" 
                  />
                  <Circle cx="150" cy="170" r="3" fill="#A0A0A0" />
                  <Path d="M165 163 L175 155" stroke="#B0B0B0" strokeWidth="2" />
                  <Circle cx="175" cy="155" r="4" fill="#D0D0D0" />
                  
                  {/* RV/Camper */}
                  <Rect x="280" y="155" width="50" height="20" rx="2" fill="#C8C8C8" />
                  <Rect x="285" y="160" width="15" height="10" fill="#E8E8E8" />
                  <Rect x="305" y="160" width="15" height="10" fill="#E8E8E8" />
                  <Circle cx="290" cy="175" r="4" fill="#A0A0A0" />
                  <Circle cx="320" cy="175" r="4" fill="#A0A0A0" />
                  
                  {/* Ground/Grass */}
                  <Path d="M0 175 Q50 173 100 175 T200 175 T300 175 T400 175" stroke="#D8D8D8" strokeWidth="2" fill="none" />
                  <Path d="M0 178 Q50 176 100 178 T200 178 T300 178 T400 178" stroke="#E0E0E0" strokeWidth="1" fill="none" />
                </Svg>

                <Text style={styles.newsEmptyStateText}>No news added</Text>
              </View>
            </View>
          ) : (
            // News Feed
            <ScrollView
              style={styles.newsFeedScroll}
              showsVerticalScrollIndicator={true}
            >
              {combinedNewsList.map((news) => {
                const newsIdNum = parseInt(news.id) || 0;
                const isTogglingThisNews = togglingNewsId === newsIdNum;
                const isDeletingThisNews = deletingNewsId === news.id;
                return (
                  <View key={news.id} style={styles.newsItem}>
                    <TouchableOpacity 
                      style={styles.newsItemIcon}
                      onPress={() => onDeleteNews(news.id)}
                      activeOpacity={0.7}
                      disabled={isTogglingThisNews || isDeletingThisNews}
                    >
                      {isDeletingThisNews ? (
                        <ActivityIndicator size="small" color="#034895" />
                      ) : (
                        <NewsIcon />
                      )}
                    </TouchableOpacity>
                    <View style={styles.newsItemContent}>
                      <View style={styles.newsItemHeader}>
                        <Text style={styles.newsItemTitle}>{news.title}</Text>
                        <Text style={styles.newsItemDate}>{news.date}</Text>
                        <TouchableOpacity
                          onPress={() => onToggleFavorite?.(newsIdNum)}
                          disabled={isTogglingThisNews}
                          style={styles.starIconContainer}
                        >
                          {isTogglingThisNews ? (
                            <ActivityIndicator size="small" color="#034895" />
                          ) : (
                            <FavouriteStarIcon filled={news.is_favourite} />
                          )}
                        </TouchableOpacity>
                      </View>
                      <View style={styles.newsItemDescriptionRow}>
                        <Text style={styles.newsItemDescription}>{news.description}</Text>
                        <Text style={styles.newsItemCreator}>Created By {formatUserName(news.createdBy)}</Text>
                      </View>
                    </View>
                  </View>
                );
              })}
            </ScrollView>
          )}
        </View>
      </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  newsContainer: {
    flex: 1,
  },
  newsDisplaySection: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    padding: 24,
    marginTop: 24,
    marginBottom: 24,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  newsDisplayEmptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 40,
  },
  newsDisplayEmptyText: {
    fontSize: 14,
    fontWeight: '400',
    color: '#9E9E9E',
    fontFamily: FontFamily.regular,
  },
  newsCardsRow: {
    flexDirection: 'row',
    gap: 12,
    justifyContent: 'flex-start',
    flexWrap: 'wrap',
  },
  newsPaginationContainer: {
    alignItems: 'center',
    marginTop: 12,
  },
  newsFormContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    padding: 24,
  },
  newsHeader: {
    fontSize: 24,
    fontWeight: '700',
    color: '#1C1C1C',
    fontFamily: FontFamily.bold,
    marginBottom: 24,
  },
  newsContent: {
    flexDirection: 'row',
    gap: 24,
    alignItems: 'flex-start',
  },
  newsLeftCard: {
    flex: 1,
  },
  newsInputContainer: {
    gap: 16,
  },
  newsTitleInput: {
    backgroundColor: '#FFFFFF',
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#C0C0C0',
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
    fontFamily: FontFamily.regular,
    color: '#1C1C1C',
    height: 44,
  },
  inputError: {
    borderColor: '#AB2B09',
  },
  errorMessageContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: 6,
  },
  errorMessageText: {
    fontSize: 12,
    color: '#C12C01',
    fontFamily: FontFamily.regular,
  },
  newsDescriptionInput: {
    backgroundColor: '#FFFFFF',
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#C0C0C0',
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
    fontFamily: FontFamily.regular,
    color: '#1C1C1C',
    height: 120,
    textAlignVertical: 'top',
  },
  addNewsButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: '#FFFFFF',
    borderRadius: 24,
    borderWidth: 1,
    borderColor: '#005CBC',
    paddingVertical: 10,
    paddingHorizontal: 16,
    alignSelf: 'flex-end',
  },
  addNewsButtonDisabled: {
    opacity: 0.7,
  },
  addNewsButtonText: {
    fontSize: 14,
    fontWeight: '700',
    color: '#005CBC',
    fontFamily: FontFamily.bold,
  },
  newsRightCard: {
    flex: 1.5,
    backgroundColor: '#F5F9FF',
    borderRadius: 8,
    padding: 28,
    height: 400,
    maxHeight: 400,
    justifyContent: 'center',
    alignItems: 'stretch',
  },
  newsEmptyState: {
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center',
  },
  newsEmptyCard: {
    width: '100%',
    maxWidth: 800,
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    paddingVertical: 56,
    paddingHorizontal: 48,
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#000',
    shadowOpacity: 0.03,
    shadowRadius: 6,
    elevation: 1,
  },
  newsEmptyStateText: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1C1C1C',
    fontFamily: FontFamily.bold,
    marginTop: 24,
  },
  newsFeedScroll: {
    flex: 1,
    width: '100%',
  },
  newsItem: {
    flexDirection: 'row',
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    padding: 16,
    marginBottom: 12,
    gap: 12,
    borderWidth: 1,
    borderColor: '#E5E5E5',
    width: '100%',
    alignSelf: 'stretch',
  },
  newsItemIcon: {
    width: 32,
    height: 32,
    borderRadius: 4,
    backgroundColor: '#E3F2FD',
    justifyContent: 'center',
    alignItems: 'center',
  },
  newsItemContent: {
    flex: 1,
  },
  newsItemHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 4,
  },
  newsItemTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1C1C1C',
    fontFamily: FontFamily.bold,
    flex: 1,
  },
  newsItemDate: {
    fontSize: 12,
    fontWeight: '400',
    color: '#616161',
    fontFamily: FontFamily.regular,
  },
  starIconContainer: {
    width: 24,
    height: 24,
    justifyContent: 'flex-start',
    alignItems: 'flex-end',
  },
  newsItemDescriptionRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 16,
  },
  newsItemDescription: {
    fontSize: 14,
    fontWeight: '400',
    color: '#4B4B4B',
    fontFamily: FontFamily.regular,
    lineHeight: 20,
    flex: 1,
  },
  newsItemCreator: {
    fontSize: 12,
    fontWeight: '400',
    color: '#616161',
    fontFamily: FontFamily.regular,
  },
    NewsdisplayHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 24,
    },
    NewsdisplayTitle: {
      fontSize: 24,
      fontWeight: '700',
      color: '#1C1C1C',
      fontFamily: FontFamily.bold,
    },
});
